import { useId, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { PermitTrainingScreen } from './PermitTrainingScreen'
import { createInitialProgress, getRoleSteps, loadProgress, normalizeProgress, saveProgress } from './progress'
import type { JourneyProgress, JourneyStep, LearningMode, PtwLearningPageProps, TutorialContent, TutorialLesson } from './types'
import badreorLogo from './assets/badreor-logo.png'
import './learning.css'

export type { PtwLearningPageProps, TutorialContent } from './types'

function JourneyStages({
  chapters, currentChapterId, home = false,
}: {
  chapters: TutorialContent['journey']['chapters']
  currentChapterId?: string
  home?: boolean
}) {
  return (
    <ol className={`ptwl-chapters${home ? ' ptwl-home-stages' : ''}`} aria-label={home ? 'Four stages of the practice permit' : 'Permit journey stages'}>
      {chapters.map((chapter, index) => (
        <li key={chapter.id} className={chapter.id === currentChapterId ? 'is-current' : ''} aria-current={chapter.id === currentChapterId ? 'step' : undefined}>
          <span className="ptwl-stage-number" aria-hidden="true">{index + 1}</span>
          <span className="ptwl-stage-title">{chapter.title}</span>
        </li>
      ))}
    </ol>
  )
}

function ResourceIcon({ kind }: { kind: 'roles' | 'help' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {kind === 'roles' ? <><circle cx="9" cy="7" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 4.5a3 3 0 0 1 0 5.5M18 13a5 5 0 0 1 3 4.6V20" /></> : <><circle cx="12" cy="12" r="9" /><path d="M9.5 8.5a2.6 2.6 0 0 1 5 1c0 2-2.5 2-2.5 4M12 17h.01" /></>}
    </svg>
  )
}

function ReferenceLesson({ lesson, initialOpen }: { lesson: TutorialLesson; initialOpen: boolean }) {
  const [open, setOpen] = useState(initialOpen)
  return (
    <details className="ptwl-reference" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>{lesson.title}</summary>
      <div className="ptwl-reference-body">
        <p>{lesson.summary}</p>
        {lesson.sections.map((section, index) => (
          <section key={`${section.heading}-${index}`}>
            <h3>{section.heading}</h3><p>{section.body}</p>
            {section.bullets && <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul>}
          </section>
        ))}
        {lesson.remember.length > 0 && <aside><strong>Remember</strong><ul>{lesson.remember.map((item) => <li key={item}>{item}</li>)}</ul></aside>}
      </div>
    </details>
  )
}

function JourneyInstruction({
  step, headingRef, practised, onPractise, onFindTerm,
}: {
  step: JourneyStep
  headingRef: RefObject<HTMLHeadingElement | null>
  practised: boolean
  onPractise: () => void
  onFindTerm: (term: string) => void
}) {
  const [selected, setSelected] = useState<string>()
  const [hint, setHint] = useState(false)
  const screenRef = useRef<HTMLDivElement>(null)

  function selectTarget(id: string) {
    if (!step.screen.regions.some((region) => region.id === id)) return
    setSelected(id)
    if (id === step.screen.correctTargetId) onPractise()
  }

  function toggleHint() {
    setHint(!hint)
    if (!hint) requestAnimationFrame(() => {
      const target = Array.from(screenRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])
        .find((button) => button.dataset.testid === `screen-region-${step.screen.correctTargetId}`)
      target?.focus()
    })
  }

  return (
    <div className="ptwl-step-layout">
      <div className="ptwl-instruction">
        <div className="ptwl-instruction-heading"><p className="ptwl-eyebrow">Task</p><span className="ptwl-current-actor">{step.actor}</span></div>
        <h1 ref={headingRef} tabIndex={-1} data-testid="journey-step-title">{step.title}</h1>
        <p className="ptwl-action-instruction">{step.instruction}</p>
        <div className="ptwl-stop"><span aria-hidden="true">!</span><p><strong>Stop if</strong> {step.stopIf}</p></div>
        <button type="button" className="ptwl-text-button" data-testid="show-hint" aria-pressed={hint} onClick={toggleHint}>{hint ? 'Hide the hint' : 'Show me where to look'}</button>
      </div>
      <div className="ptwl-screen-column" ref={screenRef}>
        <PermitTrainingScreen screen={step.screen} selectedTargetId={selected} showHint={hint} onSelectTarget={selectTarget} />
        <p className="ptwl-practice-label" data-testid="practice-status" data-practised={practised}>{practised ? '✓ This action has been practised in this training copy.' : 'Choose a part of the training screen to try the action.'}</p>
      </div>
      <section className="ptwl-step-context" aria-label="Context and handover for this step">
        <div className="ptwl-handoff" aria-label="Current and next role">
          <span className="ptwl-handoff-person"><small>Who acts now</small><strong>{step.actor}</strong></span>
          <span className="ptwl-handoff-arrow" aria-hidden="true">→</span>
          <span className="ptwl-handoff-person"><small>Who is next</small><strong>{step.nextActor}</strong></span>
        </div>
        <div className="ptwl-why"><h2>Why it matters</h2><p>{step.why}</p></div>
        <p className="ptwl-hint-note">A hint highlights the screen. It does not mark the action as practised.</p>
        {(step.details.length > 0 || step.terms.length > 0) && (
          <details className="ptwl-step-details">
            <summary>More detail</summary>
            {step.details.length > 0 && <ul>{step.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>}
            {step.terms.length > 0 && <div className="ptwl-term-links"><span>Terms in this step</span>{step.terms.map((term) => <button type="button" key={term} onClick={() => onFindTerm(term)}>{term}</button>)}</div>}
          </details>
        )}
      </section>
    </div>
  )
}

function LearningExperience({
  content, embedded = false, initialStepId, initialLessonId, practiceAvailable = false, onOpenPractice,
  progressStorage, progressScope = 'browser',
}: PtwLearningPageProps) {
  const onAccount = progressScope === 'account'
  const prefix = `ptwl-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const [loaded] = useState(() => loadProgress(content, progressStorage))
  const initialStep = content.journey.steps.find((step) => step.id === initialStepId)
  const [progress, setProgress] = useState<JourneyProgress>(() => initialStep ? {
    ...loaded.progress, selectedRole: 'ALL', mode: 'journey', currentStepId: initialStep.id, hasStarted: true,
  } : loaded.progress)
  const [view, setView] = useState<LearningMode>(initialStep ? 'journey' : initialLessonId ? 'help' : 'home')
  const [storageUnavailable, setStorageUnavailable] = useState(loaded.storageUnavailable)
  const [resetPending, setResetPending] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [helpQuery, setHelpQuery] = useState('')
  const [helpFromJourney, setHelpFromJourney] = useState(false)
  const [referencesOpen, setReferencesOpen] = useState(Boolean(initialLessonId))
  const [finished, setFinished] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const roleBriefRef = useRef<HTMLHeadingElement>(null)
  const path = getRoleSteps(content, progress.selectedRole)
  const step = path.find((item) => item.id === progress.currentStepId) ?? path[0]
  const index = step ? path.findIndex((item) => item.id === step.id) : -1
  const role = content.roles.find((item) => item.code === progress.selectedRole)
  const canResume = progress.hasStarted || progress.mode === 'roles' || progress.mode === 'help'
  const pathRead = path.filter((item) => progress.readStepIds.includes(item.id)).length
  const pathPractised = path.filter((item) => progress.practisedStepIds.includes(item.id)).length
  const query = helpQuery.trim().toLocaleLowerCase()
  const helpTopics = content.helpTopics.filter((item) => `${item.title} ${item.answer} ${item.details ?? ''} ${(item.terms ?? []).join(' ')}`.toLocaleLowerCase().includes(query))
  const glossary = content.glossary.filter((item) => `${item.term} ${item.meaning}`.toLocaleLowerCase().includes(query))

  function focusHeading() {
    requestAnimationFrame(() => headingRef.current?.focus())
  }

  function commit(patch: Partial<JourneyProgress>) {
    const next = normalizeProgress(content, { ...progress, ...patch })
    setProgress(next)
    setStorageUnavailable(!saveProgress(content, next, progressStorage))
  }

  function goHome() {
    setView('home')
    setResetPending(false)
    setFinished(false)
    focusHeading()
  }

  function openRoles() {
    commit({ mode: 'roles' })
    setView('roles')
    setFinished(false)
    focusHeading()
  }

  function openHelp(term = '') {
    setHelpQuery(term)
    setHelpFromJourney(view === 'journey')
    commit({ mode: 'help' })
    setView('help')
    focusHeading()
  }

  function startJourney(roleCode = 'ALL') {
    const nextPath = getRoleSteps(content, roleCode)
    commit({ mode: 'journey', selectedRole: roleCode, currentStepId: nextPath[0]?.id ?? '', hasStarted: true })
    setFinished(false)
    setView('journey')
    focusHeading()
  }

  function resume() {
    setFinished(false)
    setView(progress.mode === 'home' ? 'journey' : progress.mode)
    focusHeading()
  }

  function jumpToStep(id: string) {
    commit({ mode: 'journey', currentStepId: id, hasStarted: true })
    setFinished(false)
    focusHeading()
  }

  function nextStep() {
    if (!step) return
    const readStepIds = progress.readStepIds.includes(step.id) ? progress.readStepIds : [...progress.readStepIds, step.id]
    const next = path[index + 1]
    commit({ mode: 'journey', readStepIds, currentStepId: next?.id ?? step.id, hasStarted: true })
    if (!next) setFinished(true)
    focusHeading()
  }

  function practiseStep() {
    if (!step || progress.practisedStepIds.includes(step.id)) return
    commit({ practisedStepIds: [...progress.practisedStepIds, step.id] })
  }

  function resetProgress() {
    const next = createInitialProgress(content)
    setProgress(next)
    setStorageUnavailable(!saveProgress(content, next, progressStorage))
    setView('home')
    setFinished(false)
    setResetPending(false)
    setAnnouncement(onAccount ? 'Your reading and practice progress has been reset.' : 'Your local reading and practice progress has been reset.')
    focusHeading()
  }

  return (
    <div className={`ptw-learning${embedded ? ' ptwl-embedded' : ''}`} data-learning-mode={view} data-selected-role={progress.selectedRole} data-current-step={step?.id ?? ''}>
      <a className="ptwl-skip" href={`#${prefix}-content`}>Skip to learning content</a>
      <div className="ptwl-shell">
        <header className="ptwl-topbar">
          {!embedded && <button type="button" className="ptwl-brand" onClick={goHome} aria-label="PTW learning home"><img className="ptwl-brand-logo" src={badreorLogo} width={114} height={138} alt="Badr Enhanced Oil Recovery" /></button>}
          <div className="ptwl-header-copy">
            {view === 'home' ? <h1 id={`${prefix}-home-title`} ref={headingRef} tabIndex={-1}>{content.title}</h1> : <button type="button" className="ptwl-guide-title" onClick={goHome}>PTW training</button>}
            <p className="ptwl-header-subtitle">{view === 'home' ? 'Follow a practice permit from application to closeout. Try the decisions at each step.' : 'BadrEOR · Permit to Work learning guide'}</p>
            <span className="ptwl-draft-status">Draft · HSE review pending</span>
          </div>
        </header>

        <div className="ptwl-content" id={`${prefix}-content`} role={embedded ? undefined : 'main'}>
          {view === 'home' && (
            <section className="ptwl-home" aria-labelledby={`${prefix}-home-title`}>
              <nav className="ptwl-home-shortcuts" aria-label="Quick learning options">
                <button type="button" data-testid="mobile-help-shortcut" onClick={() => openHelp()}><ResourceIcon kind="help" /><span>Questions and terms</span></button>
              </nav>
              <div className="ptwl-home-grid">
                <article className="ptwl-practice-card" data-testid="home-journey" aria-labelledby={`${prefix}-practice-title`}>
                  <div className="ptwl-practice-heading"><span className="ptwl-section-label">Practice permit</span><span className="ptwl-practice-count">4 stages · {content.journey.steps.length} steps</span></div>
                  <h2 className="ptwl-practice-title" id={`${prefix}-practice-title`} data-testid="home-job">{content.journey.job.title}</h2>
                  <p className="ptwl-job-location">{content.journey.job.location}</p>
                  <p className="ptwl-job-scope">{content.journey.job.scope}</p>
                  <div className="ptwl-primary-actions">
                    {canResume && <div className="ptwl-resume-context"><strong>Continue where you left off</strong><p>{progress.hasStarted && step ? `${progress.selectedRole === 'ALL' ? 'Permit walkthrough' : `${role?.label ?? progress.selectedRole} path`} · ${step.title}` : 'Return to your role guidance or help.'}</p></div>}
                    <div className="ptwl-button-row">
                      {canResume && <button type="button" className="ptwl-button ptwl-button-primary" data-testid="resume-journey" onClick={resume}>Resume <span aria-hidden="true">→</span></button>}
                      <button type="button" className={`ptwl-button ${canResume ? 'ptwl-button-secondary' : 'ptwl-button-primary'}`} data-testid="start-journey" onClick={() => startJourney()}>{canResume ? 'Start from first step' : 'Start walkthrough'} {!canResume && <span aria-hidden="true">→</span>}</button>
                    </div>
                    <p className="ptwl-start-note">Try the decisions on a training copy of a permit.</p>
                  </div>
                  <JourneyStages chapters={content.journey.chapters} home />
                  {progress.hasStarted && <div className="ptwl-home-progress" aria-label="Local progress on your selected path"><span><strong>{pathRead}/{path.length}</strong> steps read</span><span><strong>{pathPractised}/{path.length}</strong> actions practised</span></div>}
                  <p className="ptwl-example-note">Fictional training job · no real permit actions.</p>
                  <details className="ptwl-example-details"><summary>About this example</summary><p>{content.journey.job.notice}</p></details>
                </article>
                <aside className="ptwl-home-secondary" aria-label="Quick help and learning progress">
                  <button type="button" className="ptwl-resource-link" data-testid="find-help" onClick={() => openHelp()}><span className="ptwl-resource-icon"><ResourceIcon kind="help" /></span><span className="ptwl-resource-copy"><strong>Questions and terms</strong><span>Look up an answer or an abbreviation.</span></span><span className="ptwl-resource-arrow" aria-hidden="true">→</span></button>
                  <div className="ptwl-local-note"><strong>{storageUnavailable ? 'Keep learning here' : 'Your place is saved'}</strong><p>{storageUnavailable ? (onAccount ? 'Your progress could not be loaded. You can keep reading and practising while this page is open.' : 'Saving is unavailable in this browser. You can keep reading and practising while this page is open.') : (onAccount ? 'Reading and practice are recorded separately with your sign-in, on any device. You can return to either at any time.' : 'Reading and practice are recorded separately in this browser. You can return to either at any time.')}</p></div>
                </aside>
              </div>
            </section>
          )}

          {view === 'journey' && (
            <section className="ptwl-journey" aria-label={content.journey.title}>
              <div className="ptwl-view-nav"><button type="button" data-testid="journey-home" onClick={goHome}>← Learning home</button><span>{progress.selectedRole === 'ALL' ? 'Follow one permit' : `${role?.label ?? progress.selectedRole} path`}</span><button type="button" data-testid="journey-help" onClick={() => openHelp()}>Help with this step</button></div>
              {finished ? (
                <div className="ptwl-finish-card"><h1 ref={headingRef} tabIndex={-1} data-testid="journey-step-title">Path summary</h1><p>You’ve read {pathRead} of {path.length} steps and practised {pathPractised} actions in the training copy. You can revisit any step.</p><div className="ptwl-finish-note">{onAccount ? 'This is a self-study record kept with your sign-in.' : 'This is a local learning record.'} It does not grant a role, licence, certificate or authority to work.</div><div className="ptwl-button-row"><button type="button" className="ptwl-button ptwl-button-primary" onClick={() => startJourney(progress.selectedRole)}>Revisit this path</button><button type="button" className="ptwl-button ptwl-button-secondary" onClick={openRoles}>Explore PTW roles</button><button type="button" className="ptwl-text-button" onClick={goHome}>Back to learning home</button></div></div>
              ) : step ? (
                <>
                  <div className="ptwl-journey-card">
                    <div className="ptwl-journey-overview">
                      <div className="ptwl-journey-count"><span>Step {index + 1} of {path.length}</span><div className="ptwl-step-states"><span>{progress.readStepIds.includes(step.id) ? 'Read' : 'To read'}</span><span>{progress.practisedStepIds.includes(step.id) ? 'Practised' : 'Not practised'}</span></div></div>
                      <JourneyStages chapters={content.journey.chapters} currentChapterId={step.chapterId} />
                      <div className="ptwl-job-line">
                        <p><strong>Training job:</strong> {content.journey.job.title}</p>
                        <details className="ptwl-contents" data-testid="journey-contents">
                          <summary>Steps &amp; job details</summary>
                          <div className="ptwl-contents-body">
                            <dl>
                              <div><dt>Training job</dt><dd>{content.journey.job.title}</dd></div>
                              <div><dt>Location</dt><dd>{content.journey.job.location}</dd></div>
                              <div><dt>Scope</dt><dd>{content.journey.job.scope}</dd></div>
                              <div><dt>Method</dt><dd>{content.journey.job.method}</dd></div>
                            </dl>
                            <ol>{path.map((item, itemIndex) => <li key={item.id}><button type="button" aria-current={item.id === step.id ? 'step' : undefined} onClick={(event) => { const details = event.currentTarget.closest('details'); if (details) details.open = false; jumpToStep(item.id) }}><span>{String(itemIndex + 1).padStart(2, '0')}</span>{item.title}<small>{progress.readStepIds.includes(item.id) ? 'Read' : ''}{progress.practisedStepIds.includes(item.id) ? ' · Practised' : ''}</small></button></li>)}</ol>
                          </div>
                        </details>
                      </div>
                    </div>
                    <JourneyInstruction key={`${progress.selectedRole}-${step.id}`} step={step} headingRef={headingRef} practised={progress.practisedStepIds.includes(step.id)} onPractise={practiseStep} onFindTerm={openHelp} />
                  </div>
                  <div className="ptwl-journey-controls"><button type="button" className="ptwl-button ptwl-button-secondary" data-testid="journey-back" onClick={() => index > 0 ? jumpToStep(path[index - 1].id) : goHome()}>← Back</button><p><strong>Continue marks this step as read.</strong><span>Trying the screen is separate. You can continue at any time.</span></p><button type="button" className="ptwl-button ptwl-button-primary" data-testid="journey-next" onClick={nextStep}>{index + 1 === path.length ? 'Finish this path' : 'Continue'} <span aria-hidden="true">→</span></button></div>
                </>
              ) : <div className="ptwl-finish-card"><h1 ref={headingRef} tabIndex={-1}>No steps are listed for this role yet</h1><p>Follow the complete permit journey instead.</p><button type="button" className="ptwl-button ptwl-button-primary" onClick={() => startJourney()}>Follow one permit</button></div>}
            </section>
          )}

          {view === 'roles' && (
            <section aria-labelledby={`${prefix}-roles-title`}>
              <div className="ptwl-view-nav"><button type="button" data-testid="journey-home" onClick={goHome}>← Learning home</button><button type="button" data-testid="journey-help" onClick={() => openHelp()}>Find an answer</button></div>
              <h1 id={`${prefix}-roles-title`} ref={headingRef} tabIndex={-1}>PTW roles</h1><p className="ptwl-page-intro">See who prepares, authorises, validates and holds a permit. Select a role to read its duties and practise its part in the example.</p>
              <div className="ptwl-roles-layout"><div className="ptwl-role-cards" aria-label="Choose a role">{content.roles.filter((item) => item.code !== 'ALL').map((item) => <button type="button" key={item.code} data-testid={`role-${item.code}`} aria-pressed={progress.selectedRole === item.code} className={`ptwl-role-card${progress.selectedRole === item.code ? ' is-selected' : ''}`} onClick={() => { commit({ selectedRole: item.code, mode: 'roles' }); requestAnimationFrame(() => roleBriefRef.current?.focus()) }}><strong>{item.label} <small>{item.code}</small></strong><span>{item.taskLabel}</span><span className="ptwl-role-arrow" aria-hidden="true">→</span></button>)}</div>
                {role && role.code !== 'ALL' ? <article className="ptwl-role-brief"><p className="ptwl-eyebrow">Role in the permit process</p><h2 ref={roleBriefRef} tabIndex={-1}>{role.label}</h2><p className="ptwl-role-summary">{role.summary}</p><dl>{[{ label: 'What I receive', text: role.receives }, { label: 'What I check', text: role.checks }, { label: 'What I do', text: role.does }, { label: 'Who I hand over to', text: role.handsTo }, { label: 'When I stop', text: role.stopIf }].map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.text}</dd></div>)}</dl><button type="button" className="ptwl-button ptwl-button-primary" data-testid="start-role-journey" onClick={() => startJourney(role.code)}>Practise these {getRoleSteps(content, role.code).length} steps <span aria-hidden="true">→</span></button><p className="ptwl-fineprint">A focused view of the same fictional job, with the other roles shown at handover.</p></article> : <div className="ptwl-role-placeholder"><span aria-hidden="true">↳</span><h2>Choose a PTW role</h2><p>Select a role to see its duties, checks and handovers in the permit process.</p></div>}
              </div>
            </section>
          )}

          {view === 'help' && (
            <section aria-labelledby={`${prefix}-help-title`}>
              <div className="ptwl-view-nav"><button type="button" data-testid="journey-home" onClick={goHome}>← Learning home</button>{(helpFromJourney || progress.hasStarted) && <button type="button" data-testid="return-to-journey" onClick={() => { commit({ mode: 'journey' }); setView('journey'); focusHeading() }}>Back to my step →</button>}</div>
              <h1 id={`${prefix}-help-title`} ref={headingRef} tabIndex={-1}>Questions and terms</h1>
              <section className="ptwl-role-reference" aria-labelledby={`${prefix}-role-reference-title`}>
                <div><h2 id={`${prefix}-role-reference-title`}>Who does what?</h2><p>See the duties and handovers of each PTW role.</p></div>
                <button type="button" className="ptwl-button ptwl-button-secondary" data-testid="learn-my-role" onClick={openRoles}>View PTW roles</button>
              </section>
              <div className="ptwl-help-search"><label htmlFor={`${prefix}-search`}>Search questions and terms</label><div><input id={`${prefix}-search`} type="search" placeholder="For example: suspended, handover or PH" data-testid="help-search" value={helpQuery} onChange={(event) => setHelpQuery(event.target.value)} /></div></div>
              <div className="ptwl-help-results" role="status" aria-live="polite">{helpTopics.length} {helpTopics.length === 1 ? 'answer' : 'answers'} · {glossary.length} {glossary.length === 1 ? 'term' : 'terms'}{query ? ' found' : ''}</div>
              <div className="ptwl-help-columns"><div><h2>Common questions</h2>{helpTopics.length ? helpTopics.map((topic) => <details className="ptwl-answer" key={topic.id}><summary>{topic.title}</summary><div><p>{topic.answer}</p>{topic.details && <p>{topic.details}</p>}</div></details>) : <p className="ptwl-empty">No matching questions. Try a shorter word or look in the glossary.</p>}</div><div><h2>Words &amp; abbreviations</h2>{glossary.length ? <dl className="ptwl-glossary">{glossary.map((entry) => <div key={entry.term}><dt>{entry.term}</dt><dd>{entry.meaning}</dd></div>)}</dl> : <p className="ptwl-empty">No matching terms. Clear the search to see the glossary.</p>}</div></div>
              <details className="ptwl-reference-library" open={referencesOpen} onToggle={(event) => setReferencesOpen(event.currentTarget.open)}><summary><span>Further reading</span><small>Full lesson references, when you want more detail</small></summary><div>{content.lessons.map((lesson) => <ReferenceLesson key={lesson.id} lesson={lesson} initialOpen={lesson.id === initialLessonId} />)}</div></details>
              {practiceAvailable && onOpenPractice && <div className="ptwl-platform-practice"><p><strong>Ready to explore the platform?</strong> Try the full workflow in the separate training area.</p><button type="button" className="ptwl-button ptwl-button-secondary" onClick={onOpenPractice}>Open platform practice ↗</button></div>}
            </section>
          )}
        </div>

        <aside className="ptwl-learning-boundary"><p><strong>Training only.</strong> This draft cannot authorise work, appoint a PTW role or change a live permit.</p></aside>
        {storageUnavailable && <p className="ptwl-storage-notice" role="status">Browser storage is unavailable. You can keep learning; progress may be lost when this page is closed or reloaded.</p>}
        <footer className="ptwl-footer"><p>{onAccount ? 'Self-study record with your sign-in' : 'Local learning record'} · Content v{content.version}</p>{!resetPending ? <button type="button" className="ptwl-text-button" data-testid="reset-progress" onClick={() => setResetPending(true)}>Reset my learning progress</button> : <div className="ptwl-reset-confirm" role="group" aria-label="Confirm learning progress reset"><p>{onAccount ? 'Clear your saved place, read steps and practised actions on every device you use?' : 'Clear your saved place, read steps and practised actions in this browser?'}</p><button type="button" className="ptwl-text-button" data-testid="confirm-reset" onClick={resetProgress}>Yes, reset progress</button><button type="button" className="ptwl-text-button" onClick={() => setResetPending(false)}>Keep progress</button></div>}</footer>
        <div className="ptwl-sr-only" role="status" aria-live="polite">{announcement}</div>
      </div>
    </div>
  )
}

/** Guided, browser-local learning. Only the gated host button can open platform practice. */
export function PtwLearningPage(props: PtwLearningPageProps) {
  return <LearningExperience key={`${props.content.id}:${props.content.version}:${props.initialStepId ?? ''}:${props.initialLessonId ?? ''}`} {...props} />
}

export default PtwLearningPage
